// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import { type WorkOrder, useEvolveGet, type StrongExtract } from '@evolvemep/api-ts';
import { Flex } from '@mantine/core';

import { GridMenu } from 'components/Mantine/GridMenu';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { ACTIONS_REMAINING_ON_PAGE } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/common';
import {
  type WorkOrderAction,
  useCanEditWorkOrder,
} from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';
import { useWorkOrderStateModals } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useWorkOrderStateModals';
import {
  WORK_ORDER_ACTIONS,
  type WorkOrderActionDef,
} from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/workOrderActions';
import { getWorkOrderStatusVerbiage } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/WorkOrderRejectedModal';
import { useWorkOrderModals } from 'modules/Shop/WorkOrders/WorkOrdersPage/modalRegistries';
import { useWorkOrders } from 'modules/Shop/WorkOrders/WorkOrdersPage/useWorkOrders';

type Props = {
  workOrder: WorkOrder;
  onWorkOrderUpdated: (workOrder: WorkOrder) => void;
};

const SECONDARY_ACTIONS = WORK_ORDER_ACTIONS.filter(
  (action): action is Extract<WorkOrderActionDef, { primary: false }> => !action.primary,
);

/**
 * The primary action (if any) renders as a prominent button; every other available action — including the
 * "view rejection/cancellation" prompts — lives in an overflow `GridMenu` and opens its modal on select.
 */
export const MobileWorkOrderActions = ({ workOrder, onWorkOrderUpdated }: Props) => {
  const { mutationRunning } = useWorkOrders();
  const { openModal } = useWorkOrderModals();
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);

  const navigate = useEvolveNavigate(workOrder.name, true);
  const { apiCall: getWorkOrder } = useEvolveGet(`shop/workOrder/${workOrder.workOrderId}`);

  const onActioned = useCallback(
    async (remainOnPage: boolean) => {
      if (remainOnPage) {
        await getWorkOrder().then(onWorkOrderUpdated);
      } else {
        void navigate('/work-orders');
      }
    },
    [getWorkOrder, navigate, onWorkOrderUpdated],
  );

  const rejectionVerbiage = getWorkOrderStatusVerbiage(workOrder.workOrderStateName);
  const onWorkOrderActioned = (action: WorkOrderAction) => onActioned(ACTIONS_REMAINING_ON_PAGE.includes(action));
  const { canViewCancellationRequest } = useWorkOrderStateModals({ workOrder, onWorkOrderActioned });

  const primary = WORK_ORDER_ACTIONS.find(
    (action): action is StrongExtract<WorkOrderActionDef, { primary: true }> =>
      action.primary && canEditWorkOrder(action.action),
  );

  return (
    <Flex gap="sm" align="center">
      {primary && (
        <primary.Component
          workOrder={workOrder}
          onWorkOrderUpdated={onWorkOrderUpdated}
          onWorkOrderActioned={() => onActioned(ACTIONS_REMAINING_ON_PAGE.includes(primary.action))}
          disabled={mutationRunning}
          primary
          inline={false}
        />
      )}

      <GridMenu
        options={[
          ...SECONDARY_ACTIONS.map(({ action, label }) => ({
            value: action,
            label,
            hidden: !canEditWorkOrder(action),
          })),
          {
            value: 'viewRejection',
            label: `View ${rejectionVerbiage?.progressive ?? 'rejection'}`,
            hidden: isNil(rejectionVerbiage),
          },
          { value: 'viewCancellationRequest', label: 'View cancellation request', hidden: !canViewCancellationRequest },
          { value: 'traveler', label: 'Print work order traveler' },
        ]}
        variant="light"
        size="input-sm"
        iconSize="md"
        disabled={mutationRunning}
        onMenuItemSelected={(selected) => {
          if (selected === 'viewRejection') {
            openModal('workOrderRejectedModal', { workOrder });
          } else if (selected === 'viewCancellationRequest') {
            openModal('cancelWorkOrderRequestedModal', { workOrder, onWorkOrderActioned });
          } else if (selected === 'traveler') {
            openModal('workOrderTravelerModal', { workOrder });
          } else {
            const secondary = SECONDARY_ACTIONS.find(({ action }) => action === selected);
            if (isNotNil(secondary)) {
              openModal(secondary.modal, {
                workOrder,
                onWorkOrderActioned: () => onActioned(ACTIONS_REMAINING_ON_PAGE.includes(secondary.action)),
              });
            }
          }
        }}
      />
    </Flex>
  );
};
